import { Form } from '../Components/Form';
import * as React from 'react';
import { AutostahlLocation, Make, Salutation, ServiceBookingForm, StepProps } from '../../types';
import api from '../api';
import { useServiceBookingState } from '../context/ServiceBookingState';
import { useTranslation } from 'react-i18next';

export default function Review({ form, setForm, onStepCompleted, navigateToStep }: StepProps) {
    const { t } = useTranslation();
    const { locations, makes } = useServiceBookingState();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const selectedLocation = React.useMemo(() => {
        if (!form?.standort_betriebsnummer) return null;
        return locations.find((l) => l.betriebsnummer === form.standort_betriebsnummer) ?? null;
    }, [locations, form?.standort_betriebsnummer]);

    const selectedMake = makes.find((m) => m.fabrikat_code === form.fahrzeug.fabrikat_code) ?? null;

    const formatValue = (v: any): string => {
        if (v === null || v === undefined || v === '') return '—';
        return String(v);
    };

    const formatDate = (isoOrDateLike?: string | null) => {
        if (!isoOrDateLike) return '—';
        try {
            const d = new Date(isoOrDateLike);
            if (Number.isNaN(d.getTime())) return formatValue(isoOrDateLike);
            return d.toLocaleDateString('de-AT', { year: 'numeric', month: '2-digit', day: '2-digit' });
        } catch {
            return formatValue(isoOrDateLike);
        }
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await api.postBookService(form);
            onStepCompleted();
        } finally {
            setIsSubmitting(false);
        }
    };

    const fullName = `${humanSalutationAddress(form?.customer?.anrede!, t)} ${form?.customer?.leading_title} ${
        form?.customer?.first_name
    } ${form?.customer?.last_name}`;

    function formatDateTime(dateTime: string) {
        if (!dateTime) return '—';

        try {
            const d = new Date(dateTime);
            if (Number.isNaN(d.getTime())) return formatValue(dateTime);
            return d.toLocaleString('de-AT', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return formatValue(dateTime);
        }
    }

    // Edit handlers that clear selection and navigate back
    const handleEditFahrzeug = () => {
        navigateToStep(0);
    };

    const handleEditCustomer = () => {
        navigateToStep(1);
    };

    const handleEditStandortAndTermin = () => {
        // Clear both standort and termin selections so user can re-select
        setForm((f) => ({
            ...f,
            standort_betriebsnummer: null,
            servicetermin: null,
        }));
        navigateToStep(2);
    };

    return (
        <Form className="relative mx-auto max-w-4xl" onSubmit={handleSubmit}>
            {/* Submitting overlay */}
            {isSubmitting && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80">
                    <div
                        className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"
                        role="status"
                        aria-label={t('review.submitting')}
                    ></div>
                </div>
            )}
            <h2 className="mb-6 text-2xl font-bold text-secondary-100">{t('review.title')}</h2>

            {/* Top Row: Fahrzeug and Persönliche Daten cards (Concept 1 style) */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {/* Fahrzeug Card */}
                <div className="flex flex-col border border-secondary-500 bg-white p-6">
                    <h4 className="text-lg font-bold text-secondary-100">{t('review.vehicle')}</h4>
                    <div className="mt-4 flex-1 space-y-3">
                        <DataRow label={t('field.make.label')} value={selectedMake?.marke} />
                        <DataRow label={t('field.model.label')} value={formatValue(form?.fahrzeug?.modell)} />
                        <DataRow label={t('field.licensePlate.label')} value={formatValue(form?.fahrzeug?.kennzeichen)} />
                        <DataRow label={t('field.vin.label')} value={formatValue(form?.fahrzeug?.fgstnr)} />
                        <DataRow label={t('field.mileage.label')} value={formatValue(form?.fahrzeug?.km)} />
                        <DataRow label={t('field.firstRegistration.label')} value={formatDate(form?.fahrzeug?.erstzulassung)} />
                    </div>
                    <div className="mt-4 border-t border-secondary-600 pt-4">
                        <button
                            type="button"
                            className="text-sm font-medium text-primary underline-offset-2 hover:underline"
                            onClick={handleEditFahrzeug}
                        >
                            {t('action.edit')}
                        </button>
                    </div>
                </div>

                {/* Persönliche Daten Card */}
                <div className="flex flex-col border border-secondary-500 bg-white p-6">
                    <h4 className="text-lg font-bold text-secondary-100">{t('review.personalData')}</h4>
                    <div className="mt-4 flex-1 space-y-3">
                        <DataRow label={t('review.customer')} value={fullName} />
                        <DataRow label={t('field.email.label')} value={formatValue(form?.customer?.email)} />
                        <DataRow label={t('field.phone.label')} value={form?.customer?.telefon_privat || form?.customer?.telefon_mobil || '—'} />
                        <DataRow
                            label={t('review.address')}
                            value={`${formatValue(form?.customer?.street)}, ${formatValue(form?.customer?.post_code)} ${formatValue(form?.customer?.city)}`}
                        />
                    </div>
                    <div className="mt-4 border-t border-secondary-600 pt-4">
                        <button
                            type="button"
                            className="text-sm font-medium text-primary underline-offset-2 hover:underline"
                            onClick={handleEditCustomer}
                        >
                            {t('action.edit')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Wunschtermin Highlight Box (Concept 3 style) - includes Standort */}
            <div className="mt-6 border-l-4 border-primary bg-primary/5 p-6">
                <div className="flex items-center justify-between">
                    <h4 className="text-lg font-bold text-primary">{t('review.appointment')}</h4>
                    <button
                        type="button"
                        className="border border-primary px-4 py-1 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white"
                        onClick={handleEditStandortAndTermin}
                    >
                        {t('action.edit')}
                    </button>
                </div>

                {/* Standort info at the top of the box */}
                <div className="mt-4 pb-4">
                    <div className="text-sm font-medium text-secondary-300">{t('review.location')}</div>
                    <div className="mt-1 font-semibold text-secondary-100">{selectedLocation?.standort}</div>
                    <div className="text-sm font-semibold text-secondary-300">{selectedLocation?.adresse}</div>
                </div>

                {/* Termin info */}
                <div className="text-sm font-medium text-secondary-300">{t('review.pickup')}</div>
                <div className="mt-1 text-lg font-semibold text-secondary-100">
                    {formatDateTime(form.servicetermin!.annahmezeitpunkt)}
                </div>
            </div>

            <div className="mt-10 border-t border-secondary-500 pt-6">
                <button type="submit" className="disabled:opacity-50" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? t('review.submitting') : t('action.book')}
                </button>
            </div>
        </Form>
    );
}

// Helper Components
function DataRow({ label, value }: { label: string; value?: string | null }) {
    return (
        <div>
            <div className="text-xs font-medium uppercase text-secondary-300">{label}</div>
            <div className="font-medium text-secondary-100">{value || '—'}</div>
        </div>
    );
}

function humanSalutationAddress(salutation: Salutation, t: (key: string) => string) {
    if (salutation === 'M') {
        return t('field.salutation.mr');
    } else if (salutation === 'F') {
        return t('field.salutation.mrs');
    } else if (salutation === 'FIR') {
        return t('field.salutation.company');
    }
    return '';
}
